import React, { useState } from "react";
import { Camera, Upload, Dog, Loader } from "lucide-react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../firebase";
import { useTheme } from "../contexts/ThemeContext";

const DogBreedDetector = ({ user }) => {
  const { themeClasses } = useTheme();
  const [isDogBreedAnalyzing, setIsDogBreedAnalyzing] = useState(false);
  const [dogBreedResult, setDogBreedResult] = useState(null);
  const [dogBreedImage, setDogBreedImage] = useState(null);

  const functions = getFunctions(app);

  const handleDogBreedUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Show uploaded image
    const imageUrl = URL.createObjectURL(file);
    setDogBreedImage(imageUrl);

    setIsDogBreedAnalyzing(true);
    setDogBreedResult(null);

    try {
      // Convert to base64
      const imageBase64 = await convertToBase64(file);

      // Call the detectDogBreed function
      const detectDogBreedFn = httpsCallable(functions, "detectDogBreed");
      const response = await detectDogBreedFn({
        imageBase64,
      });

      setDogBreedResult(response.data);
    } catch (error) {
      console.error("Detection failed:", error);
      setDogBreedResult({
        success: false,
        error: "Failed to detect dog breed: " + error.message,
      });
    } finally {
      setIsDogBreedAnalyzing(false);
    }
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      console.log(
        "Converting to base64:",
        file.name,
        file.type,
        "Size:",
        file.size
      );

      // Create canvas for image compression
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();

      img.onload = function () {
        try {
          // Calculate new dimensions (max 800px width/height)
          const maxSize = 800;
          let { width, height } = img;

          if (width > height) {
            if (width > maxSize) {
              height = (height * maxSize) / width;
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width = (width * maxSize) / height;
              height = maxSize;
            }
          }

          // Set canvas size and draw compressed image
          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);

          // Convert to base64 with compression (0.7 quality)
          const compressedBase64 = canvas.toDataURL("image/jpeg", 0.7);

          // Remove data:image/jpeg;base64, prefix
          const base64 = compressedBase64.split(",")[1];

          console.log(
            "Image compressed from",
            file.size,
            "to ~",
            Math.round(base64.length * 0.75),
            "bytes"
          );
          resolve(base64);
        } catch (error) {
          console.error("Compression error:", error);
          // Fallback to simple conversion
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => {
            const base64 = reader.result.split(",")[1];
            resolve(base64);
          };
          reader.onerror = reject;
        }
      };

      img.onerror = (error) => {
        console.error("Image load error:", error);
        // Fallback to simple conversion
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          const base64 = reader.result.split(",")[1];
          resolve(base64);
        };
        reader.onerror = reject;
      };

      // Load the image
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (e) => {
        img.src = e.target.result;
      };
      reader.onerror = reject;
    });
  };

  const resetDogBreedDetector = () => {
    setDogBreedResult(null);
    setDogBreedImage(null);
    setIsDogBreedAnalyzing(false);
  };

  return (
    <div className="space-y-6">
      <h2
        className={`text-2xl font-bold ${themeClasses.text.primary} flex items-center`}
      >
        <Dog className={`w-8 h-8 ${themeClasses.text.accent} mr-3`} />
        Dog Breed AI Detection
      </h2>

      <div
        className={`${themeClasses.bg.card} rounded-lg border ${themeClasses.border.primary} p-8`}
      >
        {!dogBreedImage ? (
          /* Upload Section */
          <div className="text-center">
            <Camera
              className={`w-20 h-20 ${themeClasses.text.accent} mx-auto mb-6`}
            />
            <h3
              className={`text-2xl font-bold ${themeClasses.text.primary} mb-4`}
            >
              Upload Dog Photo
            </h3>
            <p className={`${themeClasses.text.secondary} mb-8`}>
              Choose a clear photo of a dog to identify its breed using AI
            </p>

            <label
              className={`inline-flex items-center space-x-3 ${themeClasses.bg.button} text-white font-semibold py-4 px-8 rounded-lg cursor-pointer transition-all duration-300 transform hover:scale-105`}
            >
              <Upload className="w-6 h-6" />
              <span>Choose Photo</span>
              <input
                type="file"
                accept="image/*"
                onChange={handleDogBreedUpload}
                className="hidden"
                disabled={isDogBreedAnalyzing}
              />
            </label>
          </div>
        ) : (
          /* Results Section */
          <div className="space-y-6">
            {/* Uploaded Image */}
            <div className="text-center">
              <img
                src={dogBreedImage}
                alt="Uploaded dog"
                className={`max-w-md max-h-80 mx-auto rounded-lg border-2 ${themeClasses.border.primary}`}
              />
            </div>

            {/* Analysis Status */}
            {isDogBreedAnalyzing && (
              <div className="text-center">
                <Loader
                  className={`w-8 h-8 ${themeClasses.text.accent} mx-auto mb-4 animate-spin`}
                />
                <p className={`${themeClasses.text.accent} text-lg`}>
                  🤖 AI is analyzing the dog breed...
                </p>
                <p className={`${themeClasses.text.secondary} text-sm mt-2`}>
                  This may take a few seconds
                </p>
              </div>
            )}

            {/* Results */}
            {dogBreedResult && !isDogBreedAnalyzing && (
              <div
                className={`${themeClasses.bg.secondary} rounded-lg p-6 border ${themeClasses.border.primary}`}
              >
                {dogBreedResult.success ? (
                  <div>
                    <h4
                      className={`text-xl font-bold ${themeClasses.text.primary} mb-4`}
                    >
                      🐕 Breed Detection Results
                    </h4>

                    {/* Top Breed */}
                    <div
                      className={`${themeClasses.bg.card} rounded-lg p-4 mb-4 border ${themeClasses.border.accent}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h5
                            className={`text-2xl font-bold ${themeClasses.text.primary}`}
                          >
                            {dogBreedResult.topBreed.breed}
                          </h5>
                          <p className={themeClasses.text.secondary}>
                            Most likely breed
                          </p>
                        </div>
                        <div className="text-right">
                          <p
                            className={`text-3xl font-bold ${themeClasses.text.accent}`}
                          >
                            {Math.round(
                              dogBreedResult.topBreed.confidence * 100
                            )}
                            %
                          </p>
                          <p
                            className={`text-sm ${themeClasses.text.secondary}`}
                          >
                            Confidence
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* All Results */}
                    {dogBreedResult.breeds.length > 1 && (
                      <div>
                        <h6
                          className={`text-lg font-semibold ${themeClasses.text.primary} mb-3`}
                        >
                          Other Possibilities:
                        </h6>
                        <div className="space-y-2">
                          {dogBreedResult.breeds
                            .slice(1)
                            .map((breed, index) => (
                              <div
                                key={index}
                                className={`flex justify-between items-center ${themeClasses.bg.secondary} rounded p-3`}
                              >
                                <span
                                  className={`${themeClasses.text.primary} font-medium`}
                                >
                                  {breed.breed}
                                </span>
                                <span className={themeClasses.text.secondary}>
                                  {Math.round(breed.confidence * 100)}%
                                </span>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  /* Error State */
                  <div className="text-center text-red-400">
                    <p className="text-lg font-semibold mb-2">
                      {dogBreedResult.error}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="text-center space-x-4">
              <button
                onClick={resetDogBreedDetector}
                className={`${themeClasses.bg.buttonSecondary} ${themeClasses.text.primary} font-semibold py-2 px-6 rounded-lg transition-colors border ${themeClasses.border.primary} hover:${themeClasses.bg.hover}`}
              >
                Try Another Photo
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Info Section */}
      <div
        className={`${themeClasses.bg.card} rounded-lg border ${themeClasses.border.primary} p-6 text-center`}
      >
        <h4 className={`text-lg font-bold ${themeClasses.text.primary} mb-2`}>
          🧠 Powered by AI
        </h4>
        <p className={`${themeClasses.text.secondary} text-sm`}>
          Using Yngvi Thor's AI Transformer for accurate dog breed
          classification
        </p>
        <p className={`${themeClasses.text.accent} text-sm mt-2`}>
          Logged in as: {user?.email}
        </p>
      </div>
    </div>
  );
};

export default DogBreedDetector;
